import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { sendReport } from '../../functions/sendReport';
import { fetchThemeData } from '../../admin/themeSlice';
import { toSentenceCase } from '../../utils/sentenceCase';
import '../rules/rules.css';
import './photobooth.css';

const API = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const LIMIT = 8;

// Must mirror the backend's EMOJI_SCORES (app.py) — display order + weights.
const REACTION_EMOJIS = ['❤️', '🔥', '😍', '👏', '👍'];

/** Pull the created/updated report's id out of whatever shape the game-server API returns it in. */
const extractReportId = (res) => {
  if (!res || typeof res !== 'object') return null;
  return res.reportId || res.id || res?.data?.reportId || res?.data?.id || null;
};

export default function PhotoboothUpload() {
  const { user }               = useSelector(state => state.auth);
  const { data: themeData }    = useSelector(state => state.theme);
  const navigate               = useNavigate();
  const dispatch                = useDispatch();

  const [gallery, setGallery]         = useState([]);
  const [total,   setTotal]           = useState(0);
  const [page,    setPage]            = useState(0);
  const [uploading, setUploading]     = useState(false);
  const [preview,   setPreview]       = useState(null);
  const [selectedFile, setSelected]   = useState(null);
  const [cameraActive, setCamera]     = useState(false);

  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const likeInFlightRef = useRef(new Set());

  const [floatEmojis, setFloatEmojis] = useState([]); // [{ id, emoji, x, y, dx, delay }]
  const floatIdRef = useRef(0);

  const publicUrl = process.env.PUBLIC_URL || "";
  const cornerStars = ["c1.png", "c2.png", "c3.png", "c4.png"];

  const authHeader = () => ({
    Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
  });

  useEffect(() => {
    if (user?.sessionId) fetchGallery(0);
  }, [user?.sessionId]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const fetchGallery = async (p) => {
    try {
      const res = await axios.post(
        `${API}/api/photobooth/gallery`,
        { userId: user.userId, page: p, limit: LIMIT },
        { headers: authHeader() },
      );
      setGallery(res.data.items || []);
      setTotal(res.data.total  || 0);
      setPage(p);
    } catch (err) {
      console.error('Gallery fetch failed', err);
    }
  };

  // ── Camera ──────────────────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width:  { ideal: 1920 },
          height: { ideal: 1920 },
          facingMode: 'user',
        },
      });
      streamRef.current = stream;
      setCamera(true);
      setPreview(null);
      setSelected(null);
      // Attach stream after the video element mounts
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch {
      Swal.fire(
        'Camera Error',
        'Unable to access camera. Please allow camera access or use the upload option.',
        'error',
      );
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamera(false);
  };

  const takeSnapshot = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setPreview(dataUrl);
    setSelected({ type: 'base64', data: dataUrl });
    stopCamera();
  };

  // ── File input ──────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      Swal.fire('Invalid file', 'Please select a JPG or PNG image.', 'error');
      return;
    }
    setSelected({ type: 'file', data: file });
    setPreview(URL.createObjectURL(file));
  };

  // ── Upload & proceed ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedFile) {
      Swal.fire('No image', 'Please take a selfie or upload a photo first.', 'warning');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('userId', user.userId);
      form.append('name',   user.name  || '');
      form.append('email',  user.email || '');
      if (selectedFile.type === 'base64') {
        form.append('image', selectedFile.data);
      } else {
        form.append('file', selectedFile.data);
      }
      await axios.post(`${API}/api/photobooth/upload`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });

      // Create a game-server report for this post so likes can award points
      // to the poster; store the returned id as the upload row's likesid.
      console.log('Creating report for new photobooth post...',user);
      try {
        const report = await sendReport({
          role: user.role,
          token: user.token,
          points: 0,
          time: "00:00",
          gameId: user.gameId,
          name: user.name,
          sessionId: user.sessionId,
          userId: user.userId,
          organizationId: user.organizationId,
          ans: "",
        });
        const likesid = extractReportId(report);
        if (likesid) {
          await axios.post(
            `${API}/api/photobooth/likeid`,
            { userId: user.userId, likesid },
            { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
          );
        }
      } catch (reportErr) {
        console.error('sendReport (create) failed', reportErr);
      }

      navigate('/photobooth/avatars');
    } catch (err) {
      Swal.fire(
        'Upload Failed',
        err.response?.data?.error || 'Something went wrong. Please try again.',
        'error',
      );
    } finally {
      setUploading(false);
    }
  };

  // ── Reactions ───────────────────────────────────────────────────────────
  const spawnBurst = (emoji) => {
    const ids = [];
    const particles = Array.from({ length: 8 }).map(() => {
      const id = floatIdRef.current++;
      ids.push(id);
      return {
        id,
        emoji,
        x: Math.random() * window.innerWidth,
        y: window.innerHeight,
        dx: (Math.random() - 0.5) * 120,
        delay: Math.random() * 300,
      };
    });
    setFloatEmojis(prev => [...prev, ...particles]);
    setTimeout(() => {
      setFloatEmojis(prev => prev.filter(p => !ids.includes(p.id)));
    }, 2600);
  };

  const handleLike = async (post, emoji) => {
    // Same emoji already picked — nothing to change.
    if (post.myReaction === emoji) return;
    if (likeInFlightRef.current.has(post.id)) return;
    const isFirstReaction = !post.liked;
    spawnBurst(emoji);
    likeInFlightRef.current.add(post.id);
    try {
      const res = await axios.post(
        `${API}/api/photobooth/like`,
        { userId: user.userId, postId: post.id, emoji },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        const reactedEmoji = res.data.emoji || emoji;
        const reactorName  = user.name || user.email || 'You';
        setGallery(prev =>
          prev.map(p => {
            if (p.id !== post.id) return p;
            const prevReaction = p.myReaction;
            const nextCounts = { ...(p.reactionCounts || {}) };
            const nextUsers  = { ...(p.reactionUsers  || {}) };

            if (prevReaction && prevReaction !== reactedEmoji) {
              if (nextCounts[prevReaction]) {
                nextCounts[prevReaction] = Math.max(0, nextCounts[prevReaction] - 1);
                if (nextCounts[prevReaction] === 0) delete nextCounts[prevReaction];
              }
              if (nextUsers[prevReaction]) {
                nextUsers[prevReaction] = nextUsers[prevReaction].filter(n => n !== reactorName);
                if (nextUsers[prevReaction].length === 0) delete nextUsers[prevReaction];
              }
            }
            if (prevReaction !== reactedEmoji) {
              nextCounts[reactedEmoji] = (nextCounts[reactedEmoji] || 0) + 1;
              nextUsers[reactedEmoji]  = [...(nextUsers[reactedEmoji] || []), reactorName];
            }

            return {
              ...p,
              liked:      true,
              myReaction: reactedEmoji,
              likes:      res.data.likes,
              reactionCounts: nextCounts,
              reactionUsers:  nextUsers,
            };
          }),
        );

        // Award the poster points (the reaction's weight) on their game-server
        // report — only on a first-time reaction, since the "likes" total
        // doesn't change just from switching which emoji was picked.
        if (isFirstReaction && post.likesid) {
          try {
            await sendReport({
              role: user.role,
              token: user.token,
              reportId: post.likesid,
              points: res.data.likes,
              time: "00:00",
            });
          } catch (reportErr) {
            console.error('sendReport (like point) failed', reportErr);
          }
        }
      }
    } catch {
    } finally {
      likeInFlightRef.current.delete(post.id);
    }
  };

  const handleDelete = async (post) => {
    const confirm = await Swal.fire({
      title: 'Delete this photo?',
      text: 'This will remove it from the gallery.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#e11d48',
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${API}/api/photobooth/delete`,
        { userId: user.userId, postId: post.id },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        setGallery(prev => prev.filter(p => p.id !== post.id));
        setTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Could not delete this photo.', 'error');
    }
  };

  if (!themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / LIMIT) || 0;
  const isMobile   = window.innerWidth <= 768;
  const bgImg      = themeData
    ? (isMobile ? themeData.background_mob : themeData.background_desk)
    : null;
  const bgStyle = bgImg
    ? {
        backgroundImage:    `url("${process.env.REACT_APP_S3_PATH}${bgImg}")`,
        backgroundSize:     '100% 100%',
        backgroundRepeat:   'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  // Same fallback pair used by every other page's primary-button gradient
  // (Avatar.jsx, Gallery.jsx, PhotoboothAvatars.jsx) — without it, an unset
  // theme.button_color produced `linear-gradient(180deg, , )`, i.e. no
  // visible button background at all.
  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#fb9905'}, ${themeData?.button_color || '#f97316'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };
  const btnOutlineStyle = {
    borderColor: themeData?.button_color || '#fb9905',
    color: themeData?.button_color || '#fb9905',
  };

  return (
    <div className="pb-page" style={bgStyle}>

      {/* ── Upload section (exact copy of the rules page UI) ────────── */}
      <div className="user-rules-container">
        <div className={`user-rules-star user-rules-star--tl`}>
          <img src={`${publicUrl}/img/${cornerStars[0]}`} alt="" />
        </div>
        <div className={`user-rules-star user-rules-star--tr`}>
          <img src={`${publicUrl}/img/${cornerStars[1]}`} alt="" />
        </div>
        <div className={`user-rules-star user-rules-star--bl`}>
          <img src={`${publicUrl}/img/${cornerStars[2]}`} alt="" />
        </div>
        <div className={`user-rules-star user-rules-star--br`}>
          <img src={`${publicUrl}/img/${cornerStars[3]}`} alt="" />
        </div>

        <div
          className="user-rules-card"
          style={{
            backgroundImage: `url(${publicUrl}/img/box.png)`,
          }}
        >
          <div className="user-rules-title-wrap">
            <div className="user-rules-big-title" style={{ color: themeData?.text_color }}>
              {toSentenceCase(themeData?.main_title) || toSentenceCase(themeData?.landing_page_title) || toSentenceCase(themeData?.themeName) || 'KPOP photobooth'}
            </div>
            {themeData?.main_title && themeData?.landing_page_title && (
              <div className="user-rules-subline" style={{ color: themeData?.text_color }}>
                {toSentenceCase(themeData.landing_page_title)}
              </div>
            )}
          </div>

          {Array.isArray(themeData?.rules) && themeData.rules.length > 0 && (
            <ul className="user-rules-list">
              {themeData.rules.map((rule, index) => (
                <li key={index}>
                  <span className="user-rule-check" aria-hidden="true">✓</span>
                  <span className="user-rule-text" style={{ color: themeData?.text_color }}>{toSentenceCase(rule)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="user-rules-upload-wrap">
          <div className="user-rules-upload-title" style={{ color: themeData?.text_color }}>
            Upload your idol moment
          </div>
          <div className="user-rules-upload-subline" style={{ color: themeData?.text_color }}>
            Drop in a clear selfie and get ready for your transformation into a K-pop superstar
          </div>

          {/* Initial buttons */}
          {!cameraActive && !preview && (
            <div className="user-rules-upload-buttons">
              <button
                className="user-rules-upload-btn"
                onClick={startCamera}
                aria-label="Click a Selfie"
              >
                <img src={`${publicUrl}/img/camera.png`} alt="" className="user-rules-upload-icon" />
              </button>
              <span className="user-rules-upload-or">or</span>
              <button
                className="user-rules-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Upload Picture"
              >
                <img src={`${publicUrl}/img/gallery.png`} alt="" className="user-rules-upload-icon" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* Live camera */}
          {cameraActive && (
            <div className="pb-camera-live">
              <video ref={videoRef} autoPlay playsInline className="pb-video" />
              <div className="pb-btn-row">
                <button
                  className="pb-btn-secondary"
                  onClick={stopCamera}
                  style={btnOutlineStyle}
                >
                  Cancel
                </button>
                <button
                  className="pb-btn"
                  onClick={takeSnapshot}
                  style={btnStyle}
                >
                  📸 Capture
                </button>
              </div>
            </div>
          )}

          {/* Preview & confirm */}
          {preview && !cameraActive && (
            <div className="pb-preview-wrap">
              <img src={preview} alt="Your photo preview" className="pb-preview-img" />
              <div className="pb-btn-row">
                <button
                  className="pb-btn-secondary"
                  onClick={() => { setPreview(null); setSelected(null); }}
                  style={btnOutlineStyle}
                >
                  Retake
                </button>
                <button
                  className="pb-btn"
                  onClick={handleSubmit}
                  disabled={uploading}
                  style={btnStyle}
                >
                  {uploading ? 'Uploading…' : 'Continue →'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Gallery ───────────────────────────────────────────────── */}
      {gallery.length > 0 && (
        <div className="pb-gallery-section">
          <h3 className="pb-gallery-title" style={{ color: themeData?.text_color }}>Gallery</h3>

          <div className="pb-grid">
            {gallery.map(item => {
              const isBlankName = !item.name || item.name.trim().toLowerCase() === "null null";
              const displayName = isBlankName ? item.email : item.name;
              return (
                <div key={item.id} className="pb-pin">
                  <div className="pb-pin-img-wrap">
                    <img src={item.imageUrl} alt={displayName} className="pb-pin-img" />
                    {item.isOwner && (
                      <button
                        type="button"
                        className="pb-pin-delete-btn"
                        onClick={() => handleDelete(item)}
                        title="Delete this photo"
                        aria-label="Delete this photo"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                  <div className="pb-pin-footer">
                    <div className="pb-reaction-row">
                      {REACTION_EMOJIS.map(emoji => {
                        const isMine = item.liked && item.myReaction === emoji;
                        const count  = item.reactionCounts?.[emoji] || 0;
                        const names  = item.reactionUsers?.[emoji] || [];
                        const tooltip = count > 0
                          ? `${emoji} (${count})\n${names.join('\n')}`
                          : emoji;
                        return (
                          <button
                            key={emoji}
                            className={`pb-reaction-btn ${isMine ? 'picked' : ''}`}
                            onClick={() => handleLike(item, emoji)}
                            title={tooltip}
                          >
                            <span className="pb-reaction-emoji">{emoji}</span>
                            {count > 0 && <span className="pb-reaction-count">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                    <span className="pb-pin-name">{displayName}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pb-pagination">
              <button
                disabled={page === 0}
                onClick={() => fetchGallery(page - 1)}
                style={btnStyle}
              >
                ← Prev
              </button>
              <span>{page + 1} / {totalPages}</span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => fetchGallery(page + 1)}
                style={btnStyle}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Floating reaction emojis (full-screen, click-to-release) ─────── */}
      <div className="pb-emoji-layer">
        {floatEmojis.map(f => (
          <span
            key={f.id}
            className="pb-float-emoji"
            style={{
              left: `${f.x}px`,
              top: `${f.y}px`,
              '--dx': `${f.dx}px`,
              animationDelay: `${f.delay}ms`,
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
